import {
    IonButton,
    IonContent,
    IonHeader,
    IonIcon,
    IonPage,
    IonProgressBar,
    IonRefresher,
    IonRefresherContent,
    IonTitle,
    IonToolbar,
    RefresherEventDetail,
    useIonRouter
} from "@ionic/react";
import {
    arrowBackOutline,
    americanFootballOutline,
    flashOutline,
    flagOutline,
    handLeftOutline,
    medalOutline,
    refreshOutline,
    shieldOutline,
    starOutline,
    trophyOutline
} from "ionicons/icons";
import { useCallback, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";

import {
    isAuthenticated
} from "../services/auth";

import {
    fantasyApiGet
} from "../services/fantasyApi";

import "./FantasyPointSettings.css";

interface PointRow {
    label: string;
    settingKey: string;
    pointsKey?: string;
    type?: "yards";
}


interface PointSection {
    title: string;
    subtitle: string;
    icon: string;
    rows: PointRow[];
}

interface FantasyPointSettingsState {
    success: boolean;
    message?: string;

    league: {
        id: string;
        name: string;
    };

    settings: Record<string, unknown>;
}

const pointSections: PointSection[] = [
    {
        title: "Pase",
        subtitle: "Puntos para quarterbacks",
        icon: americanFootballOutline,
        rows: [
            {
                label: "Yardas por pase",
                settingKey: "passYds",
                pointsKey: "passPoint",
                type: "yards"
            },
            {
                label: "Touchdown por pase",
                settingKey: "passTds"
            },
            {
                label: "Conversión de 2 puntos",
                settingKey: "conv"
            },
            {
                label: "Intercepción lanzada",
                settingKey: "passInt"
            },
            {
                label: "Pick six lanzado",
                settingKey: "pickTD"
            }
        ]
    },
    {
        title: "Carrera",
        subtitle: "Puntos por jugadas terrestres",
        icon: flashOutline,
        rows: [
            {
                label: "Yardas por carrera",
                settingKey: "rushYds",
                pointsKey: "rushPoint",
                type: "yards"
            },
            {
                label: "Touchdown por carrera",
                settingKey: "rushTds"
            },
            {
                label: "Conversión de 2 puntos",
                settingKey: "conv"
            }
        ]
    },
    {
        title: "Recepción",
        subtitle: "Puntos para receptores",
        icon: handLeftOutline,
        rows: [
            {
                label: "Recepción",
                settingKey: "rec"
            },
            {
                label: "Yardas por recepción",
                settingKey: "recYds",
                pointsKey: "recPoint",
                type: "yards"
            },
            {
                label: "Touchdown por recepción",
                settingKey: "recTds"
            },
            {
                label: "Conversión de 2 puntos",
                settingKey: "conv"
            }
        ]
    },
    {
        title: "Pateo",
        subtitle: "Puntos de kickers",
        icon: medalOutline,
        rows: [
            {
                label: "Gol de campo anotado (20–29 yardas)",
                settingKey: "fg20"
            },
            {
                label: "Gol de campo anotado (30–39 yardas)",
                settingKey: "fg30"
            },
            {
                label: "Gol de campo anotado (40–49 yardas)",
                settingKey: "fg40"
            },
            {
                label: "Gol de campo anotado (50+ yardas)",
                settingKey: "fg50"
            },
            {
                label: "Punto extra anotado",
                settingKey: "extraPoint"
            },
            {
                label: "Gol de campo fallado",
                settingKey: "fgFailed"
            },
            {
                label: "Punto extra fallado",
                settingKey: "patFailed"
            }
        ]
    },
    {
        title: "Defensa de equipo",
        subtitle: "Puntos para D/ST",
        icon: shieldOutline,
        rows: [
            {
                label: "Touchdown defensivo",
                settingKey: "defTds"
            },
            {
                label: "Captura",
                settingKey: "sack"
            },
            {
                label: "Intercepción",
                settingKey: "int"
            },
            {
                label: "Fumble recuperado",
                settingKey: "fumbleRec"
            },
            {
                label: "Safety",
                settingKey: "safety"
            },
            {
                label: "Fumble forzado",
                settingKey: "fumbleF"
            },
            {
                label: "Patada bloqueada",
                settingKey: "blockKick"
            },
            {
                label: "0 puntos permitidos",
                settingKey: "pts0"
            },
            {
                label: "1–6 puntos permitidos",
                settingKey: "pts6"
            },
            {
                label: "7–13 puntos permitidos",
                settingKey: "pts13"
            },
            {
                label: "14–20 puntos permitidos",
                settingKey: "pts20"
            },
            {
                label: "21–27 puntos permitidos",
                settingKey: "pts27"
            },
            {
                label: "28–34 puntos permitidos",
                settingKey: "pts34"
            },
            {
                label: "35 o más puntos permitidos",
                settingKey: "pts35"
            },
            {
                label: "Menos de 100 yardas permitidas",
                settingKey: "yds100"
            }
        ]
    },
    {
        title: "Equipos especiales",
        subtitle: "Defensa y retornos",
        icon: starOutline,
        rows: [
            {
                label: "Touchdown de equipos especiales",
                settingKey: "dretTds"
            },
            {
                label: "Fumble forzado",
                settingKey: "fumbleF"
            },
            {
                label: "Fumble recuperado",
                settingKey: "fumbleRec"
            },
            {
                label: "Touchdown de jugador en equipos especiales",
                settingKey: "retTds"
            }
        ]
    },
    {
        title: "Jugadas varias",
        subtitle: "Penalizaciones por pérdidas de balón",
        icon: flagOutline,
        rows: [
            {
                label: "Fumble",
                settingKey: "fumble"
            },
            {
                label: "Fumble perdido",
                settingKey: "fumbleLost"
            }
        ]
    }
];

const FantasyPointSettings: React.FC = () => {
    const router = useIonRouter();
    const history = useHistory();
    const { id } = useParams<{ id: string }>();

    function goToSettingsMenu() {
        history.replace(`/fantasySettingsMenu/${id}`);
    }


    const [leagueName, setLeagueName] = useState("Liga Fantasy");
    const [pointData, setPointData] = useState<
        Record<string, unknown>
    >({});

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");


function getPoint(settingKey: string): string {
    const value = pointData[settingKey];

    if (
        value === undefined ||
        value === null ||
        String(value).trim() === ""
    ) {
        return "0";
    }

    return String(value);
}

function formatPoints(settingKey: string): string {
    return `${getPoint(settingKey)} pts`;
}

function formatYardPoints(
    yardsKey: string,
    pointsKey?: string
): string {
    return `${getPoint(yardsKey)} yardas = ${getPoint(
        pointsKey || ""
    )} pts`;
}


    const loadSettings = useCallback(
        async function (forceRefresh = false) {
            if (!isAuthenticated()) {
                router.push("/home", "root", "replace");
                return;
            }

            try {
                setError("");

                if (!forceRefresh) {
                    setLoading(true);
                }

const result = await fantasyApiGet<
    FantasyPointSettingsState
>(
    "control_FantasyPointSettingsState.php" +
        `?id=${encodeURIComponent(id)}` +
        `&t=${Date.now()}`
);

setLeagueName(result.league.name);
setPointData(result.settings || {});
            } catch (requestError) {
                console.error(
                    "No fue posible cargar la configuración de puntos:",
                    requestError
                );

                setError(
                    "No fue posible cargar las reglas de puntuación."
                );
            } finally {
                setLoading(false);
            }
        },
        [id, router]
    );

    useEffect(
        function () {
            loadSettings();
        },
        [loadSettings]
    );

    async function handleRefresh(
        event: CustomEvent<RefresherEventDetail>
    ) {
        await loadSettings(true);
        event.detail.complete();
    }

    return (
        <IonPage>
            <IonHeader translucent>
                <IonToolbar className="point-settings-toolbar">
                    <IonButton
    slot="start"
    fill="clear"
    color="medium"
    aria-label="Regresar a configuración"
    onClick={goToSettingsMenu}
>
    <IonIcon icon={arrowBackOutline} />
</IonButton>


                    <IonTitle>Puntuación</IonTitle>
                </IonToolbar>
            </IonHeader>

            <IonContent
                fullscreen
                className="point-settings-content"
            >
                <IonRefresher
                    slot="fixed"
                    onIonRefresh={handleRefresh}
                >
                    <IonRefresherContent
                        pullingText="Desliza para actualizar"
                        refreshingText="Actualizando puntuación..."
                    />
                </IonRefresher>

                <section className="point-settings-hero">
                    <div className="point-settings-hero__icon">
                        <IonIcon icon={trophyOutline} />
                    </div>

                    <div>
                        <span>REGLAS DE LA LIGA</span>
                        <h1>{leagueName}</h1>
                        <p>
                            Configuración actual de puntos Fantasy.
                        </p>
                    </div>
                </section>

                {loading && (
                    <IonProgressBar
                        className="point-settings-progress"
                        type="indeterminate"
                    />
                )}

                {!loading && error && (
                    <main className="point-settings-main">
                        <div className="point-settings-empty">
                            <IonIcon icon={refreshOutline} />
                            <strong>No pudimos cargar la puntuación</strong>
                            <span>{error}</span>

                            <IonButton
                                fill="outline"
                                size="small"
                                onClick={function () {
                                    loadSettings(true);
                                }}
                            >
                                Reintentar
                            </IonButton>
                        </div>
                    </main>
                )}

                {!loading && !error && (
                    <main className="point-settings-main">
                        <section className="point-settings-intro">
                            <div>
                                <span>SOLO LECTURA</span>
                                <h2>Valores de puntuación</h2>
                            </div>

                            <span className="point-settings-status">
                                Activa
                            </span>
                        </section>

                        <p className="point-settings-description">
                            Los puntos que se muestran corresponden a la
                            configuración vigente de esta liga.
                        </p>

                        <section className="point-settings-list">
                            {pointSections.map(function (section) {
                                return (
                                    <article
                                        className="point-settings-card"
                                        key={section.title}
                                    >
                                        <header className="point-settings-card__header">
                                            <span>
                                                <IonIcon
                                                    icon={section.icon}
                                                />
                                            </span>

                                            <div>
                                                <h3>{section.title}</h3>
                                                <p>{section.subtitle}</p>
                                            </div>
                                        </header>

                                        <div className="point-settings-card__rows">
                                            {section.rows.map(function (
                                                row
                                            ) {
                                                return (
                                                    <div
                                                        className="point-settings-row"
                                                        key={`${section.title}-${row.settingKey}-${row.label}`}

                                                    >
                                                        <span>
                                                            {row.label}
                                                        </span>

                                                        <strong>
                                                            {row.type === "yards"
                                                                ? formatYardPoints(
                                                                    row.settingKey,
                                                                    row.pointsKey
                                                                )
                                                                : formatPoints(row.settingKey)}
                                                        </strong>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </article>
                                );
                            })}
                        </section>
                    </main>
                )}
            </IonContent>
        </IonPage>
    );
};

export default FantasyPointSettings;
